import React, { useMemo, forwardRef } from 'react'
import cx from 'classnames'
import { v4 as uuid } from 'uuid'

import { AdoptionComponent } from '../../constants/AdoptionComponent'
import cleanProps from '../../lib/clean-react-props'
import { useDsAdoptionAttribute } from '../../utils/dsAdoption'
import { CheckboxContainer } from '../forms/Checkbox/CheckboxContainer'
import CheckboxCm from '../forms/Checkbox/CheckboxContainer.module.css'
import { RadioContainer } from '../forms/Radio/RadioContainer'
import RadioCm from '../forms/Radio/RadioContainer.module.css'
import { ToggleContainer } from '../forms/Toggle/ToggleContainer'
import ToggleCm from '../forms/Toggle/ToggleContainer.module.css'

import { IndicatorProps } from './interfaces'

import cm from './Indicator.module.css'

const Indicator: React.ForwardRefRenderFunction<HTMLInputElement, IndicatorProps> = (
  {
    id,
    name,
    tabIndex,
    type,
    className,
    toggleSize,
    form,
    loading,
    checked,
    disabled,
    invalid,
    indeterminate,
    ariaLabel,
    ariaLabelledBy,
    onChange,
    ...props
  },
  ref,
) => {
  const fieldInputId = useMemo(() => id || `${uuid()}-input`, [id])
  const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.INDICATOR)

  const hasCheckBoxType = type === 'checkbox'
  const hasRadioType = type === 'radio'
  const hasToggleType = type === 'toggle'
  const hasIndeterminate = hasCheckBoxType ? indeterminate : undefined

  const hasMediumToggleSize = toggleSize === 'medium'

  const inputClasses = cx(cm.choice, className, {
    [CheckboxCm.checkbox__input]: hasCheckBoxType,

    [ToggleCm.toggle__input]: hasToggleType,
    [ToggleCm.medium]: hasToggleType && hasMediumToggleSize,
    [ToggleCm.loading]: hasToggleType && loading,

    [RadioCm.radio__input]: hasRadioType,
  })

  const inputType = type === 'toggle' ? 'checkbox' : type

  return (
    <div className={cm.indicator} {...adoptionAttribute}>
      <input
        {...cleanProps(props, [], [])}
        type={inputType}
        name={name}
        checked={checked}
        disabled={disabled}
        id={fieldInputId}
        form={form}
        onChange={onChange}
        tabIndex={tabIndex}
        className={inputClasses}
        aria-invalid={invalid}
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-indeterminate={hasIndeterminate}
        ref={ref}
      />
      {hasCheckBoxType && (
        <CheckboxContainer
          checked={checked}
          disabled={disabled}
          indeterminate={indeterminate}
          invalid={invalid}
        />
      )}
      {hasToggleType && (
        <ToggleContainer
          checked={checked}
          disabled={disabled}
          size={toggleSize}
          loading={loading}
        />
      )}
      {hasRadioType && <RadioContainer checked={checked} disabled={disabled} invalid={invalid} />}
    </div>
  )
}

export default forwardRef(Indicator)
